<template>
  <ion-modal
    :is-open="modalAbierto"
    :backdrop-dismiss="backdropDismiss"
    class="aviso-app-modal"
    @didPresent="onPresent"
    @didDismiss="onDismiss"
  >
    <div class="aviso-app-modal__wrap" v-if="aviso">
      <div class="aviso-app-modal__card">
        <img
          v-if="aviso.icono_url"
          :src="aviso.icono_url"
          alt=""
          class="aviso-app-modal__icon"
        >
        <h2 v-if="aviso.titulo" class="aviso-app-modal__title">{{ aviso.titulo }}</h2>
        <p class="aviso-app-modal__text">{{ aviso.texto }}</p>

        <ion-textarea
          v-if="aviso.modo === 'texto_libre'"
          v-model="textoLibre"
          class="aviso-app-modal__input"
          :placeholder="aviso.input_placeholder || 'Escribe aquí...'"
          :rows="4"
          auto-grow
        />

        <div v-if="aviso.modo === 'botones'" class="aviso-app-modal__buttons">
          <ion-button
            v-for="boton in aviso.botones"
            :key="boton.indice"
            expand="block"
            :disabled="loading"
            @click="onBoton(boton)"
          >
            {{ boton.texto }}
          </ion-button>
        </div>

        <div v-if="aviso.modo === 'texto_libre'" class="aviso-app-modal__buttons">
          <ion-button expand="block" :disabled="loading" @click="onEnviarTexto">
            Enviar
          </ion-button>
        </div>

        <div v-if="aviso.modo === 'solo_lectura'" class="aviso-app-modal__buttons">
          <ion-button expand="block" :disabled="loading" @click="onCerrarSoloLectura">
            Entendido
          </ion-button>
        </div>

        <div v-if="aviso.mostrar_te_respondo_despues" class="aviso-app-modal__snooze">
          <ion-button
            expand="block"
            fill="clear"
            size="small"
            class="aviso-app-modal__snooze-btn"
            :disabled="loading"
            @click="onRecordarMasTarde"
          >
            {{ aviso.te_respondo_despues_texto }}
          </ion-button>
        </div>
      </div>
    </div>
  </ion-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonButton, IonModal, IonTextarea } from '@ionic/vue';
import tools from '@/composables/tools';
import { useAvisoApp } from '@/composables/useAvisoApp';
import type { AvisoAppBoton } from '@/interfaces/AvisoApp';

const router = useRouter();
const { aviso, isOpen, loading, enviarRespuesta, recordarMasTarde } = useAvisoApp();
const textoLibre = ref('');

function haySesion(): boolean {
  return !!localStorage.getItem('token');
}

const modalAbierto = computed(
  () => haySesion() && isOpen.value && aviso.value !== null
);

const backdropDismiss = computed(() => aviso.value?.backdrop_dismiss ?? false);

watch(modalAbierto, (open) => {
  if (open) {
    textoLibre.value = '';
  }
});

function onPresent(): void {
  if (!aviso.value) {
    return;
  }
  const titulo = aviso.value.titulo?.trim();
  tools().setLog(
    `Se mostró el aviso app #${aviso.value.id}${titulo ? ` (${titulo})` : ''}`
  );
}

function navigateUrl(url: string | null): void {
  if (!url || url.trim() === '') {
    return;
  }
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    window.open(trimmed, '_blank', 'noopener,noreferrer');
    return;
  }
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  void router.push(path);
}

async function onBoton(boton: AvisoAppBoton): Promise<void> {
  const ok = await enviarRespuesta({ boton: boton.indice });
  if (!ok) {
    tools().toast('No se pudo registrar tu respuesta. Inténtalo de nuevo.', 'danger');
    return;
  }
  navigateUrl(boton.url);
}

async function onEnviarTexto(): Promise<void> {
  const texto = textoLibre.value.trim();
  if (aviso.value?.input_obligatorio && texto === '') {
    tools().toast('Debes escribir algo antes de enviar.', 'warning');
    return;
  }
  const ok = await enviarRespuesta({ texto });
  if (!ok) {
    tools().toast('No se pudo enviar tu respuesta. Inténtalo de nuevo.', 'danger');
  }
}

async function onCerrarSoloLectura(): Promise<void> {
  const ok = await enviarRespuesta({});
  if (!ok) {
    tools().toast('No se pudo cerrar el aviso. Inténtalo de nuevo.', 'danger');
  }
}

async function onRecordarMasTarde(): Promise<void> {
  const ok = await recordarMasTarde();
  if (!ok) {
    tools().toast('No se pudo posponer el aviso. Inténtalo de nuevo.', 'danger');
    return;
  }
  const minutos = aviso.value?.recordar_mas_tarde_minutos ?? 180;
  tools().toast(`Te volvemos a preguntar dentro de ${etiquetaIntervalo(minutos)}.`, 'success', 4000);
}

function etiquetaIntervalo(minutos: number): string {
  if (minutos % 60 === 0) {
    const h = minutos / 60;
    return h === 1 ? '1 hora' : `${h} horas`;
  }
  if (minutos < 60) {
    return minutos === 1 ? '1 minuto' : `${minutos} minutos`;
  }
  const h = Math.floor(minutos / 60);
  const rest = minutos % 60;
  return `${h} h ${rest} min`;
}

async function onDismiss(): Promise<void> {
  if (!aviso.value?.backdrop_dismiss) {
    return;
  }
  await enviarRespuesta({});
}
</script>

<style scoped>
.aviso-app-modal {
  --background: rgba(15, 23, 42, 0.45);
  --width: 100%;
  --height: 100%;
}

.aviso-app-modal__wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 24px 16px;
  box-sizing: border-box;
}

.aviso-app-modal__card {
  width: min(100%, 420px);
  background: #fff;
  border-radius: 16px;
  padding: 20px 18px 16px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.22);
}

.aviso-app-modal__icon {
  display: block;
  width: 72px;
  height: 72px;
  object-fit: contain;
  margin: 0 auto 14px;
}

.aviso-app-modal__title {
  margin: 0 0 12px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
}

.aviso-app-modal__text {
  margin: 0 0 16px;
  white-space: pre-wrap;
  line-height: 1.45;
  color: #334155;
}

.aviso-app-modal__input {
  margin-bottom: 12px;
  --background: #f8fafc;
  --padding-start: 12px;
  --padding-end: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.aviso-app-modal__buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aviso-app-modal__snooze {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.aviso-app-modal__snooze-btn {
  --color: #64748b;
  font-size: 0.9rem;
  text-transform: none;
  margin: 0;
}
</style>
